<div class="container-fluid" style="margin-top: 15px">
  <div *ngIf="loading"><i class="fas fa-spinner fa-pulse"></i> Loading...</div>

  <p class="text-muted">
    Copy the flattened columns of a table that already has them (the <b>template</b>, e.g. the job postings) onto another table, so a common set of
    lookup columns - location, title, company - does not have to be rebuilt one column at a time. Nothing is written until you have previewed and
    confirmed, and nothing is ever deleted.
  </p>

  <ng-container *ngIf="!reviewing">
    <adm-steps [steps]="stepNames" [current]="0"></adm-steps>

    <div class="row">
      <div class="col-md-6">
        <mat-form-field class="full-width">
          <mat-label>Copy from (template table)</mat-label>
          <mat-select [(value)]="templateTable" (valueChange)="onTemplateChange()">
            <mat-option *ngFor="let t of templateOptions" [value]="t.name">{{ t.name }} <span class="text-muted">({{ t.count }} flattened columns)</span></mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-md-6">
        <mat-form-field class="full-width">
          <mat-label>Copy to (target table)</mat-label>
          <mat-select [(value)]="targetTable" (valueChange)="analyzeIfReady()">
            <mat-option *ngFor="let t of targetOptions" [value]="t.name">{{ t.name }}<span class="text-muted" *ngIf="t.count > 0"> ({{ t.count }} flattened columns already)</span></mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <div *ngIf="analyzing"><i class="fas fa-spinner fa-pulse"></i> Looking at the template's columns...</div>
    <div *ngIf="analyzeError" class="alert alert-danger">{{ analyzeError }}</div>
    <p *ngIf="!analysis && !analyzing && !analyzeError && !loading" class="text-muted">Choose a template and a target table.</p>

    <ng-container *ngIf="analysis">
      <p *ngIf="rows.length === 0" class="text-muted">The template has no flattened columns to copy.</p>

      <ng-container *ngIf="rows.length > 0">
        <div class="row">
          <div class="col-md-4">
            <mat-form-field class="full-width">
              <mat-label>Find a group or column</mat-label>
              <input matInput [(ngModel)]="groupSearch" autocomplete="off" />
            </mat-form-field>
          </div>
          <div class="col-md-8" style="margin-top: 10px">
            <button mat-stroked-button (click)="selectAllConnected()">Select all that {{ targetTable }} already reaches</button>&nbsp;
            <button mat-stroked-button (click)="clearSelection()" [disabled]="selectedRows.length === 0">Clear</button>
            <span class="text-muted" style="margin-left: 10px">{{ selectedRows.length }} group(s), {{ selectedColumnCount }} column(s) selected</span>
          </div>
        </div>

        <div *ngFor="let row of visibleRows" style="border: 1px solid #ddd; border-radius: 4px; padding: 8px 12px; margin-bottom: 8px" [style.background]="row.selected ? '#f3f8ff' : 'transparent'">
          <div style="display: flex; align-items: center">
            <mat-checkbox [(ngModel)]="row.selected" style="margin-right: 8px"></mat-checkbox>
            <div style="flex: 1; cursor: pointer" (click)="row.expanded = !row.expanded">
              <b>{{ row.group.label }}</b>
              <span class="text-muted"> - {{ row.group.columns.length }} column{{ row.group.columns.length === 1 ? "" : "s" }}:
                {{ row.group.columns[0].name }}<span *ngIf="row.group.columns.length > 1">, {{ row.group.columns[1].name }}</span><span *ngIf="row.group.columns.length > 2">, ...</span></span>
            </div>
            <span *ngIf="attachOf(row).connected" class="badge badge-success" style="margin-right: 8px">already reached</span>
            <span *ngIf="!attachOf(row).connected" class="badge badge-warning" style="margin-right: 8px">needs a connection</span>
            <i class="fas" [ngClass]="row.expanded ? 'fa-chevron-up' : 'fa-chevron-down'" style="cursor: pointer" (click)="row.expanded = !row.expanded"></i>
          </div>

          <div *ngIf="row.selected || row.expanded" style="margin-top: 8px">
            <div class="row">
              <div class="col-md-6">
                <mat-form-field class="full-width">
                  <mat-label>{{ targetTable }} joins this chain at</mat-label>
                  <mat-select [(value)]="row.joinIndex" (valueChange)="onJoinChange(row)">
                    <mat-option *ngFor="let a of row.group.attach" [value]="a.joinIndex" [disabled]="a.tooDeep">
                      {{ a.joinTable }}.{{ a.joinColumn }} <span class="text-muted">- {{ a.connected ? "already reached" : "not reached yet" }}{{ a.tooDeep ? " (too deep)" : "" }}</span>
                    </mat-option>
                  </mat-select>
                  <mat-hint *ngIf="attachOf(row).connected">Via {{ attachOf(row).connection }}</mat-hint>
                </mat-form-field>
              </div>
              <div class="col-md-6" *ngIf="!attachOf(row).connected">
                <mat-form-field class="full-width">
                  <mat-label>Column on {{ targetTable }} to connect on</mat-label>
                  <mat-select [(value)]="row.connectColumn">
                    <mat-option *ngFor="let c of row.choices" [value]="c.name">{{ c.suggested ? "&#9733; " : "" }}{{ c.name }}</mat-option>
                  </mat-select>
                  <mat-hint>Adds a many-to-one relationship to {{ attachOf(row).joinTable }}.{{ attachOf(row).joinColumn }}</mat-hint>
                </mat-form-field>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <mat-form-field class="full-width"><mat-label>Name prefix on the template</mat-label><input matInput [(ngModel)]="row.fromPrefix" autocomplete="off" /></mat-form-field>
              </div>
              <div class="col-md-3">
                <mat-form-field class="full-width"><mat-label>Replace it with</mat-label><input matInput [(ngModel)]="row.toPrefix" autocomplete="off" /></mat-form-field>
              </div>
              <div class="col-md-6">
                <mat-form-field class="full-width">
                  <mat-label>Field group on {{ targetTable }}</mat-label>
                  <input matInput [(ngModel)]="row.fieldGroup" autocomplete="off" />
                  <mat-hint>Created if it does not exist; leave blank for none</mat-hint>
                </mat-form-field>
              </div>
            </div>

            <table class="table table-sm" style="margin: 10px 0 0">
              <tr><th style="width: 30px"></th><th>Template column</th><th>Becomes</th><th>Carries</th></tr>
              <tr *ngFor="let c of row.group.columns">
                <td><mat-checkbox [checked]="row.columns.has(c.name)" (change)="toggleColumn(row, c.name, $event.checked)"></mat-checkbox></td>
                <td>{{ c.name }}</td>
                <td><b *ngIf="row.columns.has(c.name)">{{ newName(row, c.name) }}</b><span *ngIf="!row.columns.has(c.name)" class="text-muted">(not copied)</span></td>
                <td class="text-muted">{{ c.source }}</td>
              </tr>
            </table>
          </div>

          <div *ngIf="problem(row)" style="color: #b00020; margin-top: 6px"><i class="fas fa-exclamation-circle"></i> {{ problem(row) }}</div>
        </div>
        <p *ngIf="visibleRows.length === 0" class="text-muted">No group matches.</p>

        <div *ngIf="buildError" class="alert alert-danger">{{ buildError }}</div>
        <div style="margin-top: 10px">
          <button mat-raised-button class="piButton" (click)="build()" [disabled]="!canBuild">Preview {{ selectedColumnCount }} column{{ selectedColumnCount === 1 ? "" : "s" }}</button>
          <span *ngIf="building" style="margin-left: 10px"><i class="fas fa-spinner fa-pulse"></i> Building...</span>
          <span class="text-muted" style="margin-left: 10px">Nothing is written yet: the next step shows exactly what would change.</span>
        </div>
      </ng-container>
    </ng-container>
  </ng-container>

  <ng-container *ngIf="reviewing && built">
    <div class="text-muted" *ngIf="built.notes && built.notes.length > 0" style="margin-bottom: 6px">
      <div *ngFor="let n of built.notes"><i class="fas fa-info-circle"></i> {{ n }}</div>
    </div>
    <admin-adm-transfer-review
      [document]="built.document"
      firstStep="Select"
      finishLabel="Copy more columns"
      (back)="backToSelection()"
      (finished)="finish()"
      (imported)="onImported()"
      (openValidation)="openValidation.emit()"
    ></admin-adm-transfer-review>
  </ng-container>
</div>
